import { FamilyLettersTable, KnowledgeBaseTable, PaymentReportsTable } from '@app/components'
import { Paper, Tabs } from '@mantine/core'
import { IconBook, IconFileDollar, IconMail } from '@tabler/icons-react'
import { useRouter } from 'next/router'
import { type FC, useCallback, useEffect, useMemo, useState } from 'react'

const TAB_VALUES = ['payment-reports', 'family-letters', 'knowledge-base'] as const
type TabValue = (typeof TAB_VALUES)[number]
const DEFAULT_TAB: TabValue = 'payment-reports'

const isTabValue = (value: unknown): value is TabValue =>
  typeof value === 'string' && (TAB_VALUES as readonly string[]).includes(value)

export const DocumentsTabs: FC = () => {
  const router = useRouter()
  const { query: routerQuery, replace: routerReplace, pathname } = router

  const activeTab = useMemo<TabValue>(
    () => (isTabValue(routerQuery.tab) ? routerQuery.tab : DEFAULT_TAB),
    [routerQuery.tab]
  )

  // Track which tabs have been activated at least once. Once mounted, a tab body
  // stays mounted (Mantine hides inactive panels via display:none) so per-tab
  // component state — e.g. PaymentReportsTable's pagination cursor — survives
  // tab switches. This preserves the lazy-on-first-visit fetch behavior.
  const [visitedTabs, setVisitedTabs] = useState<Record<TabValue, boolean>>(() => ({
    'payment-reports': activeTab === 'payment-reports',
    'family-letters': activeTab === 'family-letters',
    'knowledge-base': activeTab === 'knowledge-base'
  }))
  useEffect(() => {
    setVisitedTabs((prev) => (prev[activeTab] ? prev : { ...prev, [activeTab]: true }))
  }, [activeTab])

  // Normalize the URL when the query param is missing or unknown.
  useEffect(() => {
    if (routerQuery.tab === activeTab) return

    void routerReplace({ pathname, query: { ...routerQuery, tab: activeTab } }, undefined, { shallow: true })
  }, [activeTab, pathname, routerQuery, routerReplace])

  const setTab = useCallback(
    (next: string | null) => {
      if (!isTabValue(next) || next === activeTab) return

      void routerReplace({ pathname, query: { ...routerQuery, tab: next } }, undefined, { shallow: true })
    },
    [activeTab, pathname, routerQuery, routerReplace]
  )

  return (
    <Paper p='lg'>
      <Tabs
        color='brand-red'
        onTabChange={setTab}
        value={activeTab}
      >
        <Tabs.List mb='lg'>
          <Tabs.Tab
            icon={<IconFileDollar size={16} />}
            value='payment-reports'
          >
            Payment Reports
          </Tabs.Tab>
          <Tabs.Tab
            icon={<IconMail size={16} />}
            value='family-letters'
          >
            Letters
          </Tabs.Tab>
          <Tabs.Tab
            icon={<IconBook size={16} />}
            value='knowledge-base'
          >
            Knowledge Base
          </Tabs.Tab>
        </Tabs.List>

        {visitedTabs['payment-reports'] && (
          <Tabs.Panel value='payment-reports'>
            <PaymentReportsTable />
          </Tabs.Panel>
        )}
        {visitedTabs['family-letters'] && (
          <Tabs.Panel value='family-letters'>
            <FamilyLettersTable />
          </Tabs.Panel>
        )}
        {visitedTabs['knowledge-base'] && (
          <Tabs.Panel value='knowledge-base'>
            <KnowledgeBaseTable />
          </Tabs.Panel>
        )}
      </Tabs>
    </Paper>
  )
}
